import React, {Suspense, useEffect, useRef} from "react";
import ReactDOM from "react-dom";
import Wishlist from "./Wishlist";
import styles from "./Wishlist.module.scss";
import NotloggedinModal from "../lib/ModalWindow/NotloggedinModal";
import WishlistIsFullModal from "./Modals/WishlistIsFullModal";
import AddedToWishlistModal from "./Modals/AddedToWishlistModal";
import {useAppDispatch, useCustomerSelector, useWishlistSelector} from "../../store";
import {createWishlist, updateWishlistState} from "../../store/slices/wishlishSlice";
import ErrorBoundary from "../utils/ErrorBoundary";

const WishlistPortalComponent: React.FC<{
  wishlistIcon: HTMLAnchorElement;
  wishlistPageContainer: HTMLDivElement | null;
}> = ({wishlistIcon, wishlistPageContainer}) => {
  const {customer} = useCustomerSelector();
  const dispatch = useAppDispatch();
  const {wishlist, isNotLoggedInModalShown, itemIdToShowOnPopup, itemIdToBeAdded} = useWishlistSelector();
  const wishlistIntentRef = useRef<boolean>(false);

  const redirectToWishlist = () => {
    location.href = wishlistIcon.href ?? "";
  };

  const onCustomerLoggedIn = () => {
    dispatch(updateWishlistState({isNotLoggedInModalShown: false}));
    if (wishlistIntentRef.current) {
      redirectToWishlist();
    }
  };

  const redirectFromAddedPopup = () => {
    if (itemIdToShowOnPopup) closeAddedPopup();
    redirectToWishlist();
  };

  const closeAddedPopup = () => {
    dispatch(updateWishlistState({itemIdToShowOnPopup: undefined}));
  };

  const closeNotLoggedInModal = () => {
    dispatch(updateWishlistState({isNotLoggedInModalShown: false}));
    if (wishlistPageContainer) {
      location.href = document.referrer || location.origin;
    }
  };

  // show modal on certain conditions when customer is not logged in
  useEffect(() => {
    // when customer navigates to 'wunschlisten.html' by link/bookmark
    if (!customer.isRecognised && !!wishlistPageContainer) {
      wishlistIntentRef.current = true;
      dispatch(updateWishlistState({isNotLoggedInModalShown: true}));
    }

    if (customer.isRecognised && !!itemIdToBeAdded) {
      void dispatch(createWishlist({visitorId: customer.visitorId, itemId: itemIdToBeAdded})).then(() => {
        dispatch(updateWishlistState({itemIdToBeAdded: undefined, itemIdToShowOnPopup: itemIdToBeAdded}));
      });
    }

    // on wishlist heart click
    const loginOrRedirect = (event: MouseEvent) => {
      event.preventDefault();
      wishlistIntentRef.current = true;
      if (!customer.isRecognised) {
        dispatch(updateWishlistState({isNotLoggedInModalShown: true}));
      } else {
        onCustomerLoggedIn();
      }
    };
    wishlistIcon.addEventListener("click", loginOrRedirect);

    return () => {
      wishlistIcon.removeEventListener("click", loginOrRedirect);
    };
  }, [customer.isRecognised]);

  // badge with item count
  const Badge: React.FC = () => (
    <span className={styles.badgeGreen} style={{width: (wishlist?.items.length ?? 0) > 99 ? "20px" : "14px"}}>
      {wishlist?.items.length}
    </span>
  );

  return (
    <React.Fragment>
      {!!wishlist?.items.length && ReactDOM.createPortal(<Badge />, wishlistIcon)}
      {!!wishlistPageContainer && ReactDOM.createPortal(<Wishlist />, wishlistPageContainer)}
      {/*/ show modal when user is not logged in and wants to enter wishlist */}
      <NotloggedinModal
        isVisible={isNotLoggedInModalShown}
        closeFunction={closeNotLoggedInModal}
        onLoggedIn={onCustomerLoggedIn}
      />
      <WishlistIsFullModal />
      <AddedToWishlistModal continueFunction={redirectFromAddedPopup} closeFunction={closeAddedPopup} />
    </React.Fragment>
  );
};

const WishlistPortal: React.FC = () => {
  const wishlistIcon = document.querySelector<HTMLAnchorElement>(".header-wishlist");
  const wishlistPageContainer = document.querySelector<HTMLDivElement>(".wishlist");

  if (!wishlistIcon) {
    return null;
  }

  return (
    <ErrorBoundary fallback={null} occurrence="WishlistPortal">
      <Suspense fallback={null}>
        <WishlistPortalComponent wishlistIcon={wishlistIcon} wishlistPageContainer={wishlistPageContainer} />
      </Suspense>
    </ErrorBoundary>
  );
};

export default WishlistPortal;
